// ─── Le preferenze del socio, scelte sul bot ─────────────────────────────────
//
// 🗣️ 25/09/2026, voci 12 e 13 del bot (mockup approvato): il socio dice **quando
// gioca di solito** (le disponibilità) e **cosa vuole ricevere** (le notifiche).
// *«Se uno è un po' smanettone può andare là dentro e attivarle e disattivarle …
// se no le fa fare al bot, parlandoci.»*
//
// 🔄 28/09/2026 — **«Notifiche ai soci: testi e frequenza»**, mockup «Notifiche ai
// soci» approvato (*«mockup approvato, scelte consigliate, procedi»*). Il bot
// chiede **una cosa al giorno** a chi è appena entrato, e le notifiche diventano
// quattro: 🙋 partite aperte, 🏆 tornei ed eventi, 📅 i miei appuntamenti, 🎓
// lezioni. Ognuna ha anche «decido dopo», e il bot sa **quando** il socio l'ha
// toccata l'ultima volta (`changedAt`), per non richiedergliela il giorno dopo.
// ⛔ I promemoria dell'assistente spariscono dal ponte: quello che c'è scritto
// resta nella colonna, ma non esce più.
//
// ⭐ **STANNO NELLA SCHEDA DEL SOCIO**, nella colonna `members.preferences` che
// c'era già (jsonb, «dati morbidi su cui nessuno interroga»): la segreteria le
// vede nella scheda, e il bot le rilegge a ogni giro — non ne tiene copia.
// Due chiavi sue, `disponibilita` e `notifiche`: le altre che la colonna porta
// (quelle arrivate col vecchio gestionale) non si toccano.
//
// 🙋 16/10/2026 («Trovami N giocatori»): due chiavi in più, `tipi_di_partita`
// (`M`, `MM`, `F`, `MF`) e `livelli` (`sotto`, `mio`, `sopra`). Il sesso che i
// tipi dicono va nella scheda (`members.gender`), non qui: ⇒ la rotta.
//
// ⚖️ `null` vuol dire **«non ha ancora scelto»**, ed è diverso da «no» e da
// «decido dopo»: a chi non ha scelto il bot fa la domanda, a chi ha detto
// «dopo» la rifà più avanti, a chi ha detto «no» non la rifà.
import { z } from 'zod';
/**
 * Le quattro fasce del giorno. 🔄 28/09/2026: è arrivato **pranzo**, e le ore
 * sono cambiate — mattina prima delle 12, pranzo dalle 12 alle 15, pomeriggio
 * dalle 15 alle 18, sera dalle 18. Nel weekend il bot chiede solo mattina,
 * pomeriggio e sera; un «pranzo» che arrivasse lo stesso si tiene.
 *
 * ⚖️ Le disponibilità scritte prima (mattina, pomeriggio, sera) restano buone:
 * le parole sono le stesse, e cambia solo dove passa il confine.
 */
export const FASCE_DEL_GIORNO = ['mattina', 'pranzo', 'pomeriggio', 'sera'];
const fasce = z.array(z.enum(FASCE_DEL_GIORNO)).max(4);
/**
 * 🔄 16/10/2026 (bot, mockup «Disponibilità più semplice»: 🗣️ *«nel weekend leva
 * la sera»*): nel weekend il bot offre solo **mattina e pomeriggio**. Una sera
 * (o un pranzo) scritta prima resta nella colonna finché qualcuno non riscrive le
 * disponibilità: allora cade, dal bot come dalla scheda.
 */
export const FASCE_DEL_WEEKEND = ['mattina', 'pomeriggio'];
/**
 * 🙋 Le partite aperte del suo livello: tutte subito, una al giorno, no, o decide dopo.
 * 🔄 20/10/2026 (bot, mockup «Notifiche in ordine», approvato: *«1A 2A 3A»*):
 * come gli inviti degli amici, «Tutti, subito» al posto di «fino a 2 a
 * settimana»; il `'2'` di prima si legge `'7'` (1A, `partiteDiOggi`).
 */
export const SCELTE_PARTITE = ['tutti', '7', 'no', 'dopo'];
/** 🏆 Tornei ed eventi: ogni 15 giorni, ogni settimana, no, o decide dopo. */
export const SCELTE_EVENTI = ['15', '7', 'no', 'dopo'];
/** 📅 I suoi appuntamenti: il ricordo della partita e della lezione. */
export const SCELTE_APPUNTAMENTI = ['si', 'no', 'dopo'];
/** 🎓 Le lezioni libere e i posti che si liberano con un maestro. */
export const SCELTE_LEZIONI = ['si', 'no', 'dopo'];
/**
 * 🔔 18/10/2026 (bot, mockup «Notifiche in tre», approvato: *«sì, approvo le
 * notifiche»*): 🗣️ *«avrò piano piano tanti amici … Decido io quali notifiche
 * ricevere e con quale frequenza»*. Gli **inviti degli amici**: tutti subito, il
 * primo del giorno, o nessuno (l'invito resta in 🎾 Partite). `null` ⇒ non ha
 * scelto, e per il bot vale «no».
 */
export const SCELTE_AMICI = ['tutti', 'uno', 'no'];
/** 🔔 18/10/2026: **chi entra e chi esce** dalle sue partite. `null` ⇒ non ha scelto, e vale «sì». */
export const SCELTE_FORMAZIONE = ['si', 'no'];
/**
 * 🎓 18/10/2026 (bot, mockup «Home e Lezioni», approvato): «🎓 Le mie lezioni», per
 * lezioni e allenamenti. Il **promemoria del giorno** e **chi entra e chi esce**,
 * accesi di partenza: `null` ⇒ non ha scelto, e vale «sì».
 */
export const SCELTE_LEZIONI_MIE = ['si', 'no'];
/**
 * ⏳ **Le parole del bot di prima**, accettate finché il deploy dei due non è
 * finito: `matches: 'si'` e `'2'` valgono «una al giorno», `events: '30'` vale «ogni
 * 15 giorni», e `reminders` si accetta e si lascia cadere. Un bot vecchio che
 * scrive con le parole sue non deve ricevere un 400 per una scelta del socio.
 */
const PARTITE_DI_PRIMA = ['si', '2'];
const EVENTI_DI_PRIMA = ['30'];
const PROMEMORIA_DI_PRIMA = ['8', '13', 'no'];
/**
 * 🆕 28/09/2026 (Partite Aperte, mockup approvato): al primo ingresso il bot
 * chiede se il socio vuole **organizzare** partite, **partecipare** o **tutte e
 * due**. La risposta spiega il perché delle domande sul livello con le sue parole.
 */
export const RUOLI_NEL_BOT = ['organizza', 'partecipa', 'entrambi'];
/**
 * 🙋 16/10/2026 («Trovami N giocatori», dal bot): **che partite il socio vuole
 * farsi proporre**. Quattro scelte, due per parte, e le due parti non si
 * mescolano: `M` maschile e `MM` misto da uomo, oppure `F` femminile e `MF`
 * misto da donna. ⇒ Dalla scelta il circolo ricava il sesso (`sessoDeiTipi`).
 */
export const TIPI_DI_PARTITA_DEL_SOCIO = ['M', 'MM', 'F', 'MF'];
const DA_UOMO = ['M', 'MM'];
const DA_DONNA = ['F', 'MF'];
/**
 * 🎾 16/10/2026: **di che livello** le vuole: una fascia sotto la sua, la sua,
 * una fascia sopra.
 * 🔄 17/10/2026 (dal committente: *«il tuo e quello più basso del tuo … se sei un
 * intermedio, il base e il principiante»*): la sua e **tutte** quelle sotto
 * (`sotto` = una, `sotto2` = due, …). «sopra» resta per le scelte già salvate,
 * ma il bot non la propone più.
 */
export const LIVELLI_DA_PROPORRE = ['mio', 'sotto', 'sotto2', 'sotto3', 'sotto4', 'sopra'];
/** Il sesso che i tipi scelti dicono: `null` se sono vuoti o se mescolano le due parti. */
export function sessoDeiTipi(tipi) {
    if (tipi.length === 0)
        return null;
    if (tipi.every((t) => DA_UOMO.includes(t)))
        return 'M';
    if (tipi.every((t) => DA_DONNA.includes(t)))
        return 'F';
    return null;
}
const tipiDaScrivere = z
    .array(z.enum(TIPI_DI_PARTITA_DEL_SOCIO))
    .min(1, 'Serve almeno un tipo di partita')
    .max(4)
    .refine((t) => sessoDeiTipi(t) !== null, {
    message: 'I tipi di partita stanno da una parte sola: M e MM, oppure F e MF.',
});
const livelliDaScrivere = z
    .array(z.enum(LIVELLI_DA_PROPORRE))
    .min(1, 'Serve almeno un livello')
    .max(LIVELLI_DA_PROPORRE.length);
/** Le quattro notifiche, coi nomi del ponte. */
const NOTIFICHE = [
    'matches',
    'events',
    'appointments',
    'lessons',
    'friends',
    'lineup',
    'lessonReminders',
    'lessonLineup',
];
/** Come le preferenze escono sul ponte, verso il bot. */
export const preferenzeDelPonteSchema = z.object({
    availability: z.object({ weekday: fasce, weekend: fasce, updatedAt: z.string().nullable() }).nullable(),
    notifications: z.object({
        matches: z.enum(SCELTE_PARTITE).nullable(),
        events: z.enum(SCELTE_EVENTI).nullable(),
        appointments: z.enum(SCELTE_APPUNTAMENTI).nullable(),
        lessons: z.enum(SCELTE_LEZIONI).nullable(),
        /** 🔔 18/10/2026: gli inviti degli amici. `null` ⇒ non ha scelto (per il bot, «no»). */
        friends: z.enum(SCELTE_AMICI).nullable(),
        /** 🔔 18/10/2026: chi entra e chi esce dalle sue partite. `null` ⇒ non ha scelto (vale «sì»). */
        lineup: z.enum(SCELTE_FORMAZIONE).nullable(),
        /** 🎓 18/10/2026: il promemoria delle sue lezioni e dei suoi allenamenti. `null` ⇒ vale «sì». */
        lessonReminders: z.enum(SCELTE_LEZIONI_MIE).nullable(),
        /** 🎓 18/10/2026: chi entra e chi esce dalle sue lezioni e dai suoi allenamenti. `null` ⇒ vale «sì». */
        lessonLineup: z.enum(SCELTE_LEZIONI_MIE).nullable(),
        /**
         * 🕰️ L'ultima volta che il bot ha **scritto** ciascuna, anche con lo stesso
         * valore: «decido dopo» toccato ieri non si richiede oggi. `null` ⇒ mai.
         */
        changedAt: z.object({
            matches: z.string().nullable(),
            events: z.string().nullable(),
            appointments: z.string().nullable(),
            lessons: z.string().nullable(),
            friends: z.string().nullable(),
            lineup: z.string().nullable(),
            lessonReminders: z.string().nullable(),
            lessonLineup: z.string().nullable(),
        }),
    }),
    role: z.enum(RUOLI_NEL_BOT).nullable(),
    /**
     * 🙋 16/10/2026: i tipi di partita che vuole farsi proporre, in ordine
     * (`M`, `MM`, `F`, `MF`). `null` ⇒ non ha ancora scelto.
     */
    matchTypes: z.array(z.enum(TIPI_DI_PARTITA_DEL_SOCIO)).nullable(),
    /** 🎾 I livelli che vuole farsi proporre (`sotto`, `mio`, `sopra`). `null` ⇒ non ha ancora scelto. */
    levels: z.array(z.enum(LIVELLI_DA_PROPORRE)).nullable(),
    /**
     * Il sesso della scheda (`members.gender`), non della colonna: lo scrive la
     * Segreteria, oppure il bot con i tipi di partita. `null` ⇒ non si sa.
     */
    gender: z.enum(['M', 'F']).nullable(),
});
/** Quello che il bot scrive: solo le parti che cambiano. */
export const preferenzeDaScrivereSchema = z.object({
    availability: z.object({ weekday: fasce, weekend: fasce }).optional(),
    notifications: z
        .object({
        matches: z.enum([...SCELTE_PARTITE, ...PARTITE_DI_PRIMA]),
        events: z.enum([...SCELTE_EVENTI, ...EVENTI_DI_PRIMA]),
        appointments: z.enum(SCELTE_APPUNTAMENTI),
        lessons: z.enum(SCELTE_LEZIONI),
        friends: z.enum(SCELTE_AMICI),
        lineup: z.enum(SCELTE_FORMAZIONE),
        lessonReminders: z.enum(SCELTE_LEZIONI_MIE),
        lessonLineup: z.enum(SCELTE_LEZIONI_MIE),
        /** ⏳ Solo dal bot di prima: si accetta, e non si scrive. */
        reminders: z.enum(PROMEMORIA_DI_PRIMA),
    })
        .partial()
        .optional(),
    role: z.enum(RUOLI_NEL_BOT).optional(),
    /**
     * 🙋 16/10/2026. ⚠️ Da una parte sola (`M`/`MM` oppure `F`/`MF`), altrimenti
     * 400: il sesso che ne discende finisce nella scheda.
     */
    matchTypes: tipiDaScrivere.optional(),
    levels: livelliDaScrivere.optional(),
});
/**
 * 🗓️ 16/10/2026 (mockup «🗓️ Disponibilità nella scheda», approvato): **la
 * Segreteria** cambia dalla scheda del socio le stesse scelte che lui fa sul bot
 * — le fasce della settimana, quelle del weekend e i tipi di partita. Un tocco,
 * una scrittura: ogni chiave è facoltativa, e quella che manca resta com'è.
 * ⚠️ I tipi seguono le regole del bot: almeno uno, e da una parte sola.
 */
export const disponibilitaDellaSegreteriaSchema = z
    .object({
    weekday: fasce.optional(),
    weekend: z.array(z.enum(FASCE_DEL_WEEKEND)).max(2).optional(),
    matchTypes: tipiDaScrivere.optional(),
})
    .refine((c) => c.weekday !== undefined || c.weekend !== undefined || c.matchTypes !== undefined, {
    message: 'Serve almeno una scelta da salvare.',
});
/**
 * Dalla scelta della Segreteria a quello che scriverebbe il bot. Le fasce si
 * scrivono sempre a coppia (settimana e weekend): quella che non è arrivata si
 * prende da quelle di oggi. E il weekend tiene solo le fasce che il bot offre,
 * come quando lo riscrive il socio.
 */
export function preferenzeDellaSegreteria(attuali, scelta) {
    const scritte = {};
    if (scelta.weekday !== undefined || scelta.weekend !== undefined) {
        const weekend = scelta.weekend ?? attuali.availability?.weekend ?? [];
        scritte.availability = {
            weekday: scelta.weekday ?? attuali.availability?.weekday ?? [],
            weekend: weekend.filter((f) => FASCE_DEL_WEEKEND.includes(f)),
        };
    }
    if (scelta.matchTypes)
        scritte.matchTypes = scelta.matchTypes;
    return scritte;
}
export const esitoPreferenzeSchema = z.object({
    outcome: z.literal('fatto'),
    reason: z.null(),
    preferences: preferenzeDelPonteSchema,
});
// ── Le parole di prima, dette con quelle di adesso ───────────────────────────
/**
 * `'si'` di prima ⇒ «fino a 2 a settimana» (28/09/2026) ⇒ `'7'`, una al giorno
 * (20/10/2026, scelta 1A): «2 a settimana» non c'è più, e la più vicina è questa.
 */
export function partiteDiOggi(v) {
    if (v === 'si' || v === '2')
        return '7';
    return SCELTE_PARTITE.includes(v) ? v : null;
}
/** `'30'` di prima ⇒ `'15'`: «ogni mese» non c'è più, il più vicino è ogni 15 giorni. */
export function eventiDiOggi(v) {
    if (v === '30')
        return '15';
    return SCELTE_EVENTI.includes(v) ? v : null;
}
function siNoDopo(v) {
    return v === 'si' || v === 'no' || v === 'dopo' ? v : null;
}
function istante(v) {
    return typeof v === 'string' && v !== '' ? v : null;
}
function oggetto(v) {
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}
// ── Dalla colonna della scheda, e ritorno ────────────────────────────────────
const salvateSchema = z.object({
    disponibilita: z
        .object({
        feriali: fasce.catch([]),
        weekend: fasce.catch([]),
        aggiornateIl: z.string().nullable().catch(null),
    })
        .nullish()
        .catch(null),
    ruolo: z.enum(RUOLI_NEL_BOT).nullish().catch(null),
});
/**
 * Una lista di scelte dalla colonna: tiene quelle che conosce, in ordine e una
 * volta sola. Vuota, o che non è una lista ⇒ `null`, «non ha ancora scelto».
 */
function scelteDallaColonna(v, ammesse) {
    if (!Array.isArray(v))
        return null;
    const tenute = ammesse.filter((x) => v.includes(x));
    return tenute.length === 0 ? null : tenute;
}
/** I tipi di partita dalla colonna. Due parti mescolate (scritte a mano) ⇒ `null`. */
function tipiDallaColonna(v) {
    const tipi = scelteDallaColonna(v, TIPI_DI_PARTITA_DEL_SOCIO);
    return tipi && sessoDeiTipi(tipi) !== null ? tipi : null;
}
/** Le chiavi italiane nella colonna, per ciascuna notifica del ponte. */
const IN_COLONNA = {
    matches: 'partite',
    events: 'eventi',
    appointments: 'appuntamenti',
    lessons: 'lezioni',
    friends: 'amici',
    lineup: 'formazione',
    lessonReminders: 'promemoria_lezioni',
    lessonLineup: 'formazione_lezioni',
};
function unaDi(v, ammesse) {
    return typeof v === 'string' && ammesse.includes(v) ? v : null;
}
/**
 * Le preferenze del bot, lette dalla colonna `preferences` della scheda.
 *
 * ⛔ Una colonna scritta a mano, o storta, non fa cadere niente: quello che non
 * si legge vale «non ha ancora scelto».
 * ⚠️ Le notifiche si leggono **a mano**, una chiave per volta, e non con un
 * `z.enum`: le parole di prima (`'si'`, `'30'`) vanno tradotte, non buttate.
 */
export function preferenzeDelBot(colonna, 
/** 🙋 Il sesso della scheda (`members.gender`): non sta nella colonna. */
sesso = null) {
    const letto = salvateSchema.safeParse(oggetto(colonna));
    const s = letto.success ? letto.data : { disponibilita: null, ruolo: null };
    const n = oggetto(oggetto(colonna).notifiche);
    const quando = oggetto(n.aggiornate);
    return {
        availability: s.disponibilita
            ? {
                weekday: s.disponibilita.feriali,
                weekend: s.disponibilita.weekend,
                updatedAt: s.disponibilita.aggiornateIl,
            }
            : null,
        notifications: {
            matches: partiteDiOggi(n[IN_COLONNA.matches]),
            events: eventiDiOggi(n[IN_COLONNA.events]),
            appointments: siNoDopo(n[IN_COLONNA.appointments]),
            lessons: siNoDopo(n[IN_COLONNA.lessons]),
            friends: unaDi(n[IN_COLONNA.friends], SCELTE_AMICI),
            lineup: unaDi(n[IN_COLONNA.lineup], SCELTE_FORMAZIONE),
            lessonReminders: unaDi(n[IN_COLONNA.lessonReminders], SCELTE_LEZIONI_MIE),
            lessonLineup: unaDi(n[IN_COLONNA.lessonLineup], SCELTE_LEZIONI_MIE),
            changedAt: {
                matches: istante(quando[IN_COLONNA.matches]),
                events: istante(quando[IN_COLONNA.events]),
                appointments: istante(quando[IN_COLONNA.appointments]),
                lessons: istante(quando[IN_COLONNA.lessons]),
                friends: istante(quando[IN_COLONNA.friends]),
                lineup: istante(quando[IN_COLONNA.lineup]),
                lessonReminders: istante(quando[IN_COLONNA.lessonReminders]),
                lessonLineup: istante(quando[IN_COLONNA.lessonLineup]),
            },
        },
        role: s.ruolo ?? null,
        matchTypes: tipiDallaColonna(oggetto(colonna).tipi_di_partita),
        levels: scelteDallaColonna(oggetto(colonna).livelli, LIVELLI_DA_PROPORRE),
        gender: sesso,
    };
}
/**
 * La colonna dopo una scrittura del bot: le chiavi del bot cambiano, le altre
 * restano come sono.
 *
 * ⭐ Ogni notifica scritta porta con sé **la sua data**, anche se il valore è lo
 * stesso di prima: «decido dopo» ritoccato oggi è una risposta di oggi.
 * ⚖️ Dentro `notifiche` le chiavi che il bot non conosce più — i `promemoria` di
 * prima — restano dove sono: non escono, ma non si cancellano. E un
 * `reminders` del bot di prima non tocca niente.
 */
export function colonnaConLePreferenze(colonna, scritte, adessoIso) {
    const prima = oggetto(colonna);
    const attuali = preferenzeDelBot(prima);
    const dopo = { ...prima };
    if (scritte.availability) {
        dopo.disponibilita = {
            feriali: ordinate(scritte.availability.weekday),
            weekend: ordinate(scritte.availability.weekend),
            aggiornateIl: adessoIso,
        };
    }
    const w = scritte.notifications ?? {};
    const nuove = {
        matches: w.matches === undefined ? null : partiteDiOggi(w.matches),
        events: w.events === undefined ? null : eventiDiOggi(w.events),
        appointments: w.appointments ?? null,
        lessons: w.lessons ?? null,
        friends: w.friends ?? null,
        lineup: w.lineup ?? null,
        lessonReminders: w.lessonReminders ?? null,
        lessonLineup: w.lessonLineup ?? null,
    };
    const toccate = NOTIFICHE.filter((k) => nuove[k] !== null);
    if (toccate.length > 0) {
        const n = attuali.notifications;
        const date = { ...n.changedAt };
        for (const k of toccate)
            date[k] = adessoIso;
        dopo.notifiche = {
            ...oggetto(prima.notifiche),
            ...Object.fromEntries(NOTIFICHE.map((k) => [IN_COLONNA[k], nuove[k] ?? n[k]])),
            aggiornate: Object.fromEntries(NOTIFICHE.map((k) => [IN_COLONNA[k], date[k]])),
        };
    }
    if (scritte.role)
        dopo.ruolo = scritte.role;
    // 🙋 In ordine, una volta sola: la stessa scelta scritta due volte è la stessa colonna.
    if (scritte.matchTypes)
        dopo.tipi_di_partita = TIPI_DI_PARTITA_DEL_SOCIO.filter((x) => scritte.matchTypes.includes(x));
    if (scritte.levels)
        dopo.livelli = LIVELLI_DA_PROPORRE.filter((x) => scritte.levels.includes(x));
    return dopo;
}
function ordinate(f) {
    return FASCE_DEL_GIORNO.filter((x) => f.includes(x));
}
// ── Come le legge la segreteria ──────────────────────────────────────────────
/** Le fasce con le loro ore, come le legge la Segreteria nella scheda. */
export const PAROLE_FASCE = {
    mattina: 'Mattina (prima delle 12)',
    pranzo: 'Pranzo (12–15)',
    pomeriggio: 'Pomeriggio (15–18)',
    sera: 'Sera (dalle 18)',
};
export function fasceInParole(f) {
    return f.length === 0 ? 'nessuna' : f.map((x) => PAROLE_FASCE[x]).join(', ');
}
export const PAROLE_PARTITE = {
    tutti: 'sì, tutte subito',
    '7': 'sì, una al giorno',
    no: 'no',
    dopo: 'decide dopo',
};
export const PAROLE_EVENTI = {
    '15': 'sì, ogni 15 giorni',
    '7': 'sì, ogni settimana',
    no: 'no',
    dopo: 'decide dopo',
};
export const PAROLE_APPUNTAMENTI = {
    si: 'sì, il ricordo di partite e lezioni',
    no: 'no',
    dopo: 'decide dopo',
};
/** 🔔 18/10/2026: come la Segreteria legge le due scelte nuove. */
export const PAROLE_AMICI = {
    tutti: 'tutti, subito',
    uno: '1 al giorno',
    no: 'no (li trova in Partite)',
};
export const PAROLE_FORMAZIONE = { si: 'sì', no: 'no' };
export const PAROLE_LEZIONI_MIE = { si: 'sì', no: 'no' };
export const PAROLE_LEZIONI = {
    si: 'sì, lezioni libere e posti liberati',
    no: 'no',
    dopo: 'decide dopo',
};
/** 🙋 Come la Segreteria legge i tipi di partita scelti. */
export const PAROLE_TIPI_DI_PARTITA = {
    M: '👨 maschile',
    MM: '👫 misto (da uomo)',
    F: '👩 femminile',
    MF: '👫 misto (da donna)',
};
export const PAROLE_LIVELLI_DA_PROPORRE = {
    mio: 'il suo',
    sotto: 'una fascia sotto',
    sotto2: 'due fasce sotto',
    sotto3: 'tre fasce sotto',
    sotto4: 'quattro fasce sotto',
    sopra: 'una fascia sopra',
};
export const PAROLE_RUOLO = {
    organizza: 'Organizza',
    partecipa: 'Partecipa',
    entrambi: 'Organizza e partecipa',
};
//# sourceMappingURL=preferenze-bot.js.map